import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  BuySubmissionFormEvent,
  Event,
  EventType,
  HubspotUpdateEvent,
  PaymentMethod,
  SecureQuoteFormSubmissionEvent,
  SellSubmissionFormEvent,
  WebflowFormSubmissionEvent,
} from "@/core/entities/event";
import { Product, ProductType } from "@/core/entities/product";
import { formatDistanceToNow } from "date-fns";
import {
  Calendar,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  Hash,
  Mail,
  MailOpen,
  Package,
  RefreshCw,
  Shield,
  ShoppingCart,
  Tag,
  User,
} from "lucide-react";

interface ProductDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  event?: Event | null;
}

const getProductTypeIcon = (buyOrSell: ProductType) => {
  switch (buyOrSell) {
    case ProductType.BUY:
      return <Package className="h-4 w-4" />;
    case ProductType.SELL:
      return <DollarSign className="h-4 w-4" />;
    case ProductType.BOTH:
      return <Package className="h-4 w-4" />;
    default:
      return <Package className="h-4 w-4" />;
  }
};

const getProductTypeBadgeVariant = (buyOrSell: ProductType) => {
  switch (buyOrSell) {
    case ProductType.BUY:
      return "default";
    case ProductType.SELL:
      return "secondary";
    case ProductType.BOTH:
      return "outline";
    default:
      return "default";
  }
};

const formatProductType = (buyOrSell: ProductType): string => {
  switch (buyOrSell) {
    case ProductType.BUY:
      return "Buy Only";
    case ProductType.SELL:
      return "Sell Only";
    case ProductType.BOTH:
      return "Buy & Sell";
    default:
      return buyOrSell;
  }
};

const getEventTypeIcon = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return <FileText className="h-4 w-4" />;
    case EventType.SECURE_QUOTE:
      return <Shield className="h-4 w-4" />;
    case EventType.SELL_SUBMISSION_FORM:
      return <ShoppingCart className="h-4 w-4" />;
    case EventType.BUY_SUBMISSION_FORM:
      return <ShoppingCart className="h-4 w-4" />;
    case EventType.HUBSPOT_UPDATE:
      return <RefreshCw className="h-4 w-4" />;
    default:
      return <Calendar className="h-4 w-4" />;
  }
};

const formatEventType = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return "Webflow Form";
    case EventType.SECURE_QUOTE:
      return "Secure Quote";
    case EventType.SELL_SUBMISSION_FORM:
      return "Sell Submission";
    case EventType.BUY_SUBMISSION_FORM:
      return "Buy Submission";
    case EventType.HUBSPOT_UPDATE:
      return "HubSpot Update";
    default:
      return type;
  }
};

const formatPaymentMethod = (method: PaymentMethod) => {
  switch (method) {
    case PaymentMethod.PAYPAL:
      return "PayPal";
    case PaymentMethod.ECHECK:
      return "Echeck";
    case PaymentMethod.WIRE:
      return "Wire";
    default:
      return method;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatMiles = (miles: number) => {
  return new Intl.NumberFormat("en-US").format(miles);
};

export const ProductDetailsDialog = ({
  open,
  onOpenChange,
  product,
  event,
}: ProductDetailsDialogProps) => {
  if (!product) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-[600px]">
          <SheetHeader>
            <SheetTitle>Product Not Found</SheetTitle>
            <SheetDescription>
              The requested product could not be found.
            </SheetDescription>
          </SheetHeader>
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-4xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle className="flex items-center gap-2">
            {getProductTypeIcon(product.buyOrSell)}
            Event Details - {product.name}
          </SheetTitle>
          <SheetDescription>
            Comprehensive information about this miles/points product including
            pricing, ranges, and transaction details.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <div className="space-y-4">
            {event && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Event Information</h3>
                </div>

                <div className="border rounded-lg p-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {getEventTypeIcon(event.type)}
                          <Badge variant="outline">
                            {formatEventType(event.type) ||
                              formatProductType(product.buyOrSell)}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {formatDistanceToNow(event.createdAt, {
                            addSuffix: true,
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {event.createdAt.toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    {/* Event-specific data */}
                    <div className="grid grid-cols-2 gap-4">
                      {event.type === EventType.WEBFLOW_FORM_SUBMISSION && (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Hash className="h-4 w-4" />
                              Amount
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatMiles(
                                (event as WebflowFormSubmissionEvent).data
                                  ?.amount || 0,
                              )}{" "}
                              miles
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Tag className="h-4 w-4" />
                              Type
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(
                                event as WebflowFormSubmissionEvent
                              ).data?.buyOrSell?.toUpperCase() || "N/A"}
                            </p>
                          </div>
                        </>
                      )}

                      {event.type === EventType.SECURE_QUOTE && (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Hash className="h-4 w-4" />
                              Miles
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatMiles(
                                (event as SecureQuoteFormSubmissionEvent).data
                                  ?.miles || 0,
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Average Rate
                            </div>
                            <p className="text-sm text-muted-foreground">
                              $
                              {(event as SecureQuoteFormSubmissionEvent).data
                                ?.averageRate || 0}
                              /mile
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Total Payout
                            </div>
                            <p className="text-sm text-muted-foreground font-semibold">
                              {formatCurrency(
                                (event as SecureQuoteFormSubmissionEvent).data
                                  ?.totalPayout || 0,
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Tag className="h-4 w-4" />
                              Type
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(
                                event as SecureQuoteFormSubmissionEvent
                              ).data?.type?.toUpperCase() || product.buyOrSell}
                            </p>
                          </div>
                        </>
                      )}

                      {event.type === EventType.SELL_SUBMISSION_FORM && (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Hash className="h-4 w-4" />
                              Miles
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatMiles(
                                (event as SellSubmissionFormEvent).data
                                  ?.miles || 0,
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Average Rate
                            </div>
                            <p className="text-sm text-muted-foreground">
                              $
                              {(event as SellSubmissionFormEvent).data
                                ?.averageRate || 0}
                              /mile
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Total Payout
                            </div>
                            <p className="text-sm text-muted-foreground font-semibold">
                              {formatCurrency(
                                (event as SellSubmissionFormEvent).data
                                  ?.totalPayout || 0,
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <CreditCard className="h-4 w-4" />
                              Payment Method
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(event as SellSubmissionFormEvent).data
                                ?.paymentMethod
                                ? formatPaymentMethod(
                                    (event as SellSubmissionFormEvent).data
                                      .paymentMethod,
                                  )
                                : "N/A"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Hash className="h-4 w-4" />
                              Frequent Flyer #
                            </div>
                            <p className="text-sm text-muted-foreground font-mono">
                              {(event as SellSubmissionFormEvent).data
                                ?.frequentFlyerNumber || "N/A"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Mail className="h-4 w-4" />
                              Account Email
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(event as SellSubmissionFormEvent).data
                                ?.accountEmail || "N/A"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Calendar className="h-4 w-4" />
                              Miles Expiration
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(event as SellSubmissionFormEvent).data
                                ?.milesExpirationDate || "N/A"}
                            </p>
                          </div>
                          {(event as SellSubmissionFormEvent).data
                            ?.paymentMethod === PaymentMethod.PAYPAL && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <MailOpen className="h-4 w-4" />
                                Paypal Mail
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {(event as SellSubmissionFormEvent).data
                                  ?.paypalEmail || "N/A"}
                              </p>
                            </div>
                          )}
                          {((event as SellSubmissionFormEvent).data
                            ?.paymentMethod === PaymentMethod.ECHECK ||
                            (event as SellSubmissionFormEvent).data
                              ?.paymentMethod === PaymentMethod.WIRE) &&
                            (event as SellSubmissionFormEvent).data?.notes && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                  <FileText className="h-4 w-4" />
                                  Notes
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {
                                    (event as SellSubmissionFormEvent).data
                                      .notes
                                  }
                                </p>
                              </div>
                            )}
                        </>
                      )}

                      {event.type === EventType.BUY_SUBMISSION_FORM && (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Hash className="h-4 w-4" />
                              Miles
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatMiles(
                                (event as BuySubmissionFormEvent).data?.miles ||
                                  0,
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Average Rate
                            </div>
                            <p className="text-sm text-muted-foreground">
                              $
                              {(event as BuySubmissionFormEvent).data
                                ?.averageRate || 0}
                              /mile
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <DollarSign className="h-4 w-4" />
                              Total Payout
                            </div>
                            <p className="text-sm text-muted-foreground font-semibold">
                              {formatCurrency(
                                (event as BuySubmissionFormEvent).data
                                  ?.totalPayout || 0,
                              )}
                            </p>
                          </div>
                          {(event as BuySubmissionFormEvent).data?.notes && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <FileText className="h-4 w-4" />
                                Notes
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {(event as BuySubmissionFormEvent).data.notes}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {event.type === EventType.HUBSPOT_UPDATE && (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <RefreshCw className="h-4 w-4" />
                              Field Updated
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {(event as HubspotUpdateEvent).data?.fieldName ||
                                "Unknown Field"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <FileText className="h-4 w-4" />
                              Previous Value
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {String(
                                (event as HubspotUpdateEvent).data?.oldValue ??
                                  "N/A",
                              )}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <FileText className="h-4 w-4" />
                              New Value
                            </div>
                            <p className="text-sm text-muted-foreground font-semibold">
                              {String(
                                (event as HubspotUpdateEvent).data?.newValue ??
                                  "N/A",
                              )}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Product Overview</h3>
            </div>

            <div className="border rounded-lg p-6">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <h4 className="text-lg font-semibold">{product.name}</h4>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={getProductTypeBadgeVariant(product.buyOrSell)}
                      >
                        {formatProductType(product.buyOrSell)}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Product Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Tag className="h-4 w-4" />
                      Category
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {product.category}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Package className="h-4 w-4" />
                      Transaction Type
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatProductType(product.buyOrSell)}
                    </p>
                  </div>

                  {product.buyOrSell !== ProductType.BUY && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <DollarSign className="h-4 w-4" />
                        Sell Range
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {product.minimumValueForSell.toLocaleString()} -{" "}
                        {product.maximumValueForSell.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {product.buyOrSell !== ProductType.SELL && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Package className="h-4 w-4" />
                        Buy Range
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {product.minimumValueForBuy.toLocaleString()} -{" "}
                        {product.maximumValueForBuy.toLocaleString()}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      Status Tier
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {product.statusTiers[0]?.name} (
                      {product.statusTiers[0]?.percentage}%)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Calendar className="h-4 w-4" />
                      Created
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {formatDistanceToNow(product.createdAt, {
                          addSuffix: true,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {product.createdAt.toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Clock className="h-4 w-4" />
                      Last Updated
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {formatDistanceToNow(product.updatedAt, {
                          addSuffix: true,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {product.updatedAt.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Pricing Information</h3>
              </div>

              <div className="border rounded-lg p-6">
                <div className="grid grid-cols-2 gap-4">
                  {product.sellPrices.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Sell Price</p>
                      <p className="text-lg font-semibold text-green-600">
                        ${product.sellPrices[0].price} per point/mile
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Range: {product.sellPrices[0].minRange.toLocaleString()}{" "}
                        - {product.sellPrices[0].maxRange.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {product.buyPrices.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Buy Price</p>
                      <p className="text-lg font-semibold text-blue-600">
                        ${product.buyPrices[0].price} per point/mile
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Range: {product.buyPrices[0].minRange.toLocaleString()}{" "}
                        - {product.buyPrices[0].maxRange.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Event Information */}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
